import { fetchGraphql } from '@zoopla/fetch-graphql';
import { ChangeUserPasswordInput } from '../../../graphql.generated';
import {
    ChangeUserPasswordDocument,
    ChangeUserPasswordMutation
} from '../queries/changeSignInPassword.generated';
import { FetchUserDocument, FetchUserQuery } from '../queries/user.generated';
import {
    logUserAuthFailure,
    trackAuthSuccess
} from './authMutationHook.tracking';
import { getNextBestAction } from '../utils/getNextBestAction';
import { AuthMutationHook } from './authMutationHook.types';
import { useAuth } from './useAuth';

export const useEnforcedChangePassword: AuthMutationHook<
    ChangeUserPasswordInput
> = ({ trackingData }) => {
    const { setCurrentSession } = useAuth();

    const changePasswordAndAuthenticate = async (
        changePasswordInput: ChangeUserPasswordInput
    ) => {
        try {
            const { data, errors } =
                await fetchGraphql<ChangeUserPasswordMutation>(
                    ChangeUserPasswordDocument,
                    changePasswordInput
                );

            if (!data || errors) {
                throw new Error();
            }

            if (data?.changeUserPassword?.__typename === 'AuthError') {
                throw new Error();
            }

            const { data: userData, errors: userErrors } =
                await fetchGraphql<FetchUserQuery>(FetchUserDocument);

            if (!userData?.user || userErrors) {
                throw new Error();
            }

            const { user } = userData;

            // This covers the case we have seen in a few incidents with sessions
            // when the authentication API works well but the user one fails
            if (!user.loggedIn || !user.userUuid) {
                logUserAuthFailure({
                    type: 'signin',
                    subtype: !user.userUuid ? 'no_uuid' : 'not_logged_in'
                });

                throw new Error();
            }

            trackAuthSuccess({
                source: 'zpg',
                type: 'signin',
                userObject: user,
                label: trackingData.label,
                segmentData: trackingData.segmentData
            });

            setCurrentSession(user, false);

            const nextBestActions = getNextBestAction(user);

            return { ...user, actions: nextBestActions };
        } catch {
            throw new Error();
        }
    };

    return changePasswordAndAuthenticate;
};
